import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { CasestudyCard, casestudyCards } from ".";
import { CaseStudyHerosection, CaseStudyInformation, ClientFeedback, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const Dubai: React.FC = () => {
    return (
        <NewLayout
            title="Dubai Housekeeping | WhatsApp Business customer story"
            description="Discover how Dubai Housekeeping transformed their customer communication, scaled operations, and put convenience first using Gallabox's WhatsApp automation."
            canonical="https://gallabox.com/case-study/dubai-housekeeping"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping-og.png" }}
        >
            <CaseStudyHerosection
                title="Gallabox transforms Dubai Housekeeping's service excellence on WhatsApp"
                description="Discover how Dubai Housekeeping transformed their customer communication, scaled operations, and put convenience first using Gallabox's WhatsApp automation."
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping.png"}
                redirectUrl={"/case-study"}
            />

            <StatsInfo
                statsInfo={[
                    { percentage: "25%", label: "Increase in team productivity" },
                    { percentage: "47%", label: "Higher CSAT ratings" },
                    { percentage: "31%", label: "Business growth from WhatsApp" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[
                                { label: "Company", value: ["Dubai Housekeeping"] },
                                { label: "Industry", value: ["Hospitality"] },
                                { label: "Employees", value: ["50-200"] },
                                { label: "Team", value: ["Support"] },
                            ]}
                            product={{
                                title: "Product Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-shared-inbox", label: "Shared Team Inbox" },
                                    { link: "https://gallabox.com/whatsapp-ai-chatbot", label: "Gallabox Gen AI" },
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "WhatsApp Broadcast" },
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "WhatsApp Chatbot" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About">
                            <CaseStudyInformation mainTitle={"Dubai Housekeeping: Where convenience meets care"} className="mt-0" />
                            <p className="mt-2">
                                <a href="https://dubaihousekeeping.com" className="text-blue-500">
                                    Dubai Housekeeping
                                </a>{" "}
                                emerged as a solution to a critical gap in Dubai&lsquo;s service industry. Founded in 2014, they set out to become a support system for working
                                professionals who lacked the time and resources to manage their homes.
                                <br />
                                <br />
                                As they scaled, Dubai Housekeeping wanted to deploy a communication platform that could help them ramp up support operations without affecting the
                                existing business flows. &ldquo;We wanted to bring on board a comfortable, confident, reliable, trustworthy service provider,&ldquo; explains Param
                                Singh, CEO of Dubai Housekeeping, highlighting his mission to serve Dubai&apos;s busy workforce.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation
                                subTitle={"The challenge: With service excellence comes growth pains "}
                                descriptions={[
                                    "As with any business that wants to scale, Dubai Housekeeping faced the inevitable challenges of scaling. One of their first hurdles was gaining wide recognition for their service excellence. As the company expanded, they faced several operational challenges:",
                                ]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "Managing increasing customer communication volume" },
                                    { listItem: "Limited phone line capacity for customer service" },
                                    { listItem: "Need for better client history tracking" },
                                    { listItem: "Complex scheduling requirements" },
                                    { listItem: "Growing team coordination needs." },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                            <CaseStudyInformation
                                subTitle={"From a solo agent to a multi-service provider"}
                                descriptions={["Their growth story is remarkable, having started  their support operations with just one agent. The company:"]}
                                className="mt-12"
                            />

                            <FeatureList
                                List={[
                                    { listItem: "Grew its agent base to 5, setting up their initial automation with Freshdesk" },
                                    { listItem: "Expanded to 10 agents across multiple service divisions" },
                                    {
                                        listItem: "Developed specialized teams for:",
                                        nestedListItem: ["Holiday homes management", "Decluttering services", "Deep cleaning projects", "Handyman services."],
                                    },
                                ]}
                                className="mt-4 bg-gray-100"
                            />

                            <p className="mt-5">
                                Moving from 1 to 10 agents was a long journey of establishing Standard Operating Procedures (SOPs) and consolidating customer communication.
                            </p>

                            <CaseStudyInformation
                                subTitle={"How Gallabox fit the bill seamlessly"}
                                descriptions={["The decision to implement Gallabox came at a crucial growth phase, driven by several key factors:"]}
                                className="mt-12"
                            />
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Unified communication"} className="mt-0" />
                            <p className="mt-2">
                                &quot;Client communication is not about stopping at a particular interaction. It has to be correlated with what the client has already said, has
                                already been saying, and has said two months back. And Gallabox helped us with consolidating all these&quot;, says Param, highlighting the need for
                                comprehensive customer interaction history, which Gallabox makes possible with its shared WhatsApp team inbox.
                            </p>
                            <CaseStudyInformation
                                subTitle={"Enhanced team efficiency"}
                                descriptions={[
                                    "When they played around Gallabox &quot;to handle their services business on WhatsApp, they found the platform tailored for what they wanted to solve, which included",
                                ]}
                                className="mt-12"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "The ability to handle multiple customer interactions simultaneously" },
                                    { listItem: "Improved response management with canned responses" },
                                    { listItem: "Gen AI-led communication support for team members" },
                                    { listItem: "Comprehensive customer engagement tracking across touchpoints with CRM integration." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <ClientFeedback
                                descriptions={
                                    <p className="text-gray-900 italic font-semibold px-6 mb-4">
                                        &quot;Client communication is not about stopping at a particular interaction. It has to be correlated with what the client has already said,
                                        has already been saying, and has said two months back. And Gallabox helped us with consolidating all these.&quot;
                                    </p>
                                }
                                authorName="Param Singh"
                                designationWithServiceName="CEO, Dubai Housekeeping"
                                className="border-blue-500 bg-blue-100  mt-5 "
                            />

                            <CaseStudyInformation
                                subTitle={"The operational impact was tangible soon after deploying Gallabox"}
                                descriptions={["The decision to implement Gallabox came at a crucial growth phase, driven by several key factors:"]}
                                className="mt-12"
                            />
                            <p className="text-2xl text-gray-900 font-medium">Ironed out customer engagement</p>
                            <FeatureList
                                List={[
                                    { listItem: "Customer engagement through WhatsApp beat every other support channel by wide margins?" },
                                    { listItem: "They identified peak communication hours from dawn to dusk for mobilizing resources" },
                                    { listItem: "A single agent could manage multiple conversations simultaneously with chatbots and AI assistance." },
                                    { listItem: "Customer history tracking was no longer about switching tabs–everything was integrated into the shared WhatsApp team inbox." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                            <CaseStudyInformation
                                subTitle={"Increased agent productivity and conversation quality"}
                                descriptions={["The platform has supported Dubai Housekeeping's unique approach to team growth."]}
                                className="mt-10"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "It freed team members from answering basic queries, allowing them to handle more complex tasks." },
                                    {
                                        listItem: "Agents could concentrate on more revenue-generating interactions, which helped them in internal promotions and specialization.",
                                    },
                                    { listItem: "They enhanced communication quality across service lines with bot-assisted interactions and Gen AI." },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                            <p className="mt-5">
                                &quot;From this process, people become the next leaders for the next divisions. Gallabox is the base where they get trained,&quot; shares Param,
                                describing their approach to team development.
                            </p>
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"The business impact of implementing Gallabox is a story by itself"} className="mt-0" />
                            <p className="mt-2">
                                While the direct impact of automation can&apos;t be measured in simple numbers, the management emphasizes its crucial role. When asked about the
                                biggest upside of moving to Gallabox, Uttam Bhatia, Dubai Housekeeping&apos;s Co-founder said &quot;The most effective thing about Gallabox is we
                                are able to handle more pings at any given point.&quot; <br />
                                <br></br>
                                The other ways in which Gallabox has positively impacted Dubai Housekeeping include:
                            </p>
                            <FeatureList
                                List={[
                                    { listItem: "Efficient scaling from 1 to 10 agents" },
                                    { listItem: "Increase in CSAT ratings with every service delivery" },
                                    { listItem: "Maintaining of service quality while growing as a company" },
                                    { listItem: "Effectively competing with larger market players" },
                                    { listItem: "Increase in ROI and agent productivity with outcome-driven automation" },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                            <FeatureList
                                title="Result"
                                description="Leverage the power of WhatsApp (the Business API version) for your Hospitality business. It packs powerful automation features that will: "
                                List={[
                                    { listItem: "Efficient scaling from 1 to 10 agents" },
                                    { listItem: "Increase in CSAT ratings with every service delivery" },
                                    { listItem: "Maintaining of service quality while growing as a company" },
                                    { listItem: "Effectively competing with larger market players " },
                                    { listItem: "Increase in ROI and agent productivity with outcome-driven automation" },
                                ]}
                                Icon={<IoIosCheckmarkCircle color="purple" />}
                                className="mt-4 bg-purple-50 border-purple-500"
                            />
                            <CaseStudyInformation
                                subTitle={"What's in store for Dubai Housekeeping"}
                                descriptions={["Dubai Housekeeping continues to expand their WhatsApp capabilities through Gallabox, exploring business outcomes like:"]}
                                className="mt-10"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "Enhanced chatbot integration into multiple service scenarios" },
                                    { listItem: "Advanced feedback collection that make it easier to course correct and share small wins" },
                                    { listItem: "Automated responses to never let a warm lead wait and subsequently, move to competition" },
                                    { listItem: "Effectively competing with larger market players " },
                                    { listItem: "Expanded coordination tools that integrate their service functions better." },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                            <p className="mt-5">
                                The journey of Dubai Housekeeping is a classic showcase of how the right communication platforms can support not just growth, but also foster a
                                culture of development and service diversification without the pain of operational overhauls.
                            </p>
                            <ClientFeedback
                                descriptions="The most effective thing about Gallabox is we are able to handle more pings at any given point."
                                authorName="Uttam Bhatia"
                                designationWithServiceName="Co-Founder, Dubai Housekeeping"
                                className="border-blue-500 bg-blue-50 mt-4"
                            />
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCards.slice(0, 3)?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default Dubai;
